/**
 * ExecutionRowList — renders the per-row outcome of the most recent
 * execution, with retry badges and on-failure-mode indicators.
 *
 * sl-g3on.9.2: Surfaces the data the orchestrator now persists into
 * `processStage.rows[]` (sl-g3on.9.1). One row per executed input row,
 * each annotated with status, retry count and the on-failure mode that
 * was in effect.
 *
 * sl-8fwi.6 (#58 fix): When the execution contains at least one failed
 * row AND the user has not yet dismissed the hint, the reusable
 * `Coachmark` component (components/coachmark) is rendered above the
 * list. It points the user at the retry toggle in the config panel
 * via a DOM highlight (`data-coachmark-target="retry-toggle"` — the
 * attribute is set in process-tab.tsx).
 *
 * sl-8fwi.6 rework: The pre-rework banner was inline-emulated (with a
 * hard-coded emoji, no visual target link, and only user-driven
 * dismiss). The deep audit flagged all three, so this file now
 * delegates the whole banner to the reusable `Coachmark` component
 * and adds auto-dismiss when a retry resolves the last failed run.
 *
 *   ┌─ Row #1 ✓ Success                          (420 ms)
 *   ├─ Row #2 ✓ Success (after 2 retries)         (1240 ms)
 *   ├─ Row #3 ✗ Failed — Continued                (3 attempts)
 *   └─ Row #4 ⊘ Aborted  (stop mode)              (not run)
 */

import { useI18n } from '@affine/i18n';
import { cssVarV2 } from '@streamlain/theme/v2';
import type { FC } from 'react';
import { useMemo, useRef } from 'react';

import { Coachmark } from '../../../../components/coachmark';
import type { ProcessStageRowEntry } from '../../services/logging';

/**
 * sl-8fwi.6: localStorage key for the per-device dismiss flag of the
 * retry coachmark. Namespaced with the streamlain prefix so it cannot
 * collide with unrelated keys (the same prefix is used by
 * AUTOMATION_TAB_STORAGE_KEY in automation-panel.tsx).
 */
const RETRY_COACHMARK_DISMISSED_KEY = 'streamlain.coachmark.retry.dismissed';

/**
 * sl-8fwi.6 rework (AC3): CSS selector for the retry-config element
 * in process-tab.tsx. The Coachmark component adds the highlight
 * class to every matching element while the banner is visible, so
 * the user sees a pulsing outline on the retry toggle itself rather
 * than a disconnected text banner.
 */
const RETRY_COACHMARK_HIGHLIGHT_SELECTOR =
  '[data-coachmark-target="retry-toggle"]';

interface ExecutionRowListProps {
  rows: ReadonlyArray<ProcessStageRowEntry>;
}

export const ExecutionRowList: FC<ExecutionRowListProps> = ({ rows }) => {
  const t = useI18n();
  const tProxy = t as unknown as Record<string, (vars?: unknown) => string>;

  // sl-8fwi.6: Show the retry coachmark only when (a) the run actually
  // produced at least one failed/aborted row (success-only runs do not
  // need the hint) AND (b) the user has not previously dismissed it.
  const hasFailedRow = useMemo(
    () =>
      rows.some(
        r =>
          r.status === 'failed' ||
          r.status === 'abandoned' ||
          r.status === 'aborted'
      ),
    [rows]
  );

  // sl-8fwi.6 rework (AC4): Auto-dismiss the coachmark after the user
  // has successfully retried. We track the previous hasFailedRow value
  // in a ref — when the current render flips from "had failures" to
  // "all successful", we surface `autoDismissOn = true` to the
  // Coachmark component, which writes the persistent dismiss flag.
  //
  // Guarded against (a) the initial render where the ref and the
  // current value match (no transition), and (b) empty row lists where
  // the "success" state is trivial and means nothing was executed.
  const hadFailedRowRef = useRef(hasFailedRow);
  const autoDismissRetryResolved =
    hadFailedRowRef.current && !hasFailedRow && rows.length > 0;
  hadFailedRowRef.current = hasFailedRow;

  if (rows.length === 0) return null;

  return (
    <div
      data-testid="execution-row-list"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <Coachmark
        testId="retry-coachmark"
        title={tProxy['com.streamlain.streamlain.coachmark.retry.title']({})}
        body={tProxy['com.streamlain.streamlain.coachmark.retry.body']({})}
        dismissLabel={tProxy[
          'com.streamlain.streamlain.coachmark.retry.dismiss'
        ]({})}
        storageKey={RETRY_COACHMARK_DISMISSED_KEY}
        show={hasFailedRow}
        autoDismissOn={autoDismissRetryResolved}
        highlightSelector={RETRY_COACHMARK_HIGHLIGHT_SELECTOR}
      />
      {rows.map((row, idx) => (
        <ExecutionRowItem
          key={`${row.rowId}-${idx}`}
          row={row}
          index={idx + 1}
          t={t}
        />
      ))}
    </div>
  );
};

interface ExecutionRowItemProps {
  row: ProcessStageRowEntry;
  index: number;
  t: ReturnType<typeof useI18n>;
}

const ExecutionRowItem: FC<ExecutionRowItemProps> = ({ row, index, t }) => {
  const { statusIcon, statusColor } = pickStatusVisuals(row.status);
  const tProxy = t as Record<string, (vars?: unknown) => string>;

  // Pluralisation for "{count} retries". Pluralisation rules differ
  // between languages — keep it simple with one/many keys + lookup.
  const retryLabel =
    row.retryCount > 0
      ? row.retryCount === 1
        ? tProxy['com.streamlain.streamlain.execution.retry.one']({})
        : tProxy['com.streamlain.streamlain.execution.retry.many']({
            count: row.retryCount,
          })
      : null;

  const onFailureLabel = (() => {
    if (row.status === 'success') return null;
    switch (row.onFailureMode) {
      case 'continue':
        return tProxy[
          'com.streamlain.streamlain.execution.on-failure.continue'
        ]({});
      case 'stop':
        return tProxy['com.streamlain.streamlain.execution.on-failure.stop'](
          {}
        );
      default:
        return null;
    }
  })();

  // sl-8fwi.3 rework: `abandoned` is the historical persisted label; `aborted` is
  // the new distinct status introduced so the post-hoc label path can
  // be removed. The i18n key name stays `execution.abandoned` for
  // frontend string compatibility, but it now renders for both.
  const abandonedLabel =
    row.status === 'abandoned' || row.status === 'aborted'
      ? tProxy['com.streamlain.streamlain.execution.abandoned']({})
      : null;

  return (
    <div
      data-testid="execution-row-item"
      data-row-status={row.status}
      data-retry-count={row.retryCount}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
        backgroundColor: cssVarV2('layer/background/secondary'),
        borderLeft: `3px solid ${statusColor}`,
        borderRadius: '6px',
        fontSize: '12px',
        color: cssVarV2('text/primary'),
      }}
    >
      <span style={{ color: statusColor, fontWeight: 600 }}>{statusIcon}</span>
      <span style={{ minWidth: '40px', color: cssVarV2('text/tertiary') }}>
        #{index}
      </span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {row.error ?? row.rowId}
      </span>
      {retryLabel && (
        <span
          data-testid="retry-badge"
          title={retryLabel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
            padding: '2px 6px',
            borderRadius: '10px',
            background: cssVarV2('chip/label/orange'),
            color: 'white',
            fontSize: '10px',
            fontWeight: 600,
          }}
        >
          {row.retryCount}
        </span>
      )}
      {onFailureLabel && (
        <span
          data-testid="on-failure-label"
          style={{
            fontSize: '11px',
            color: cssVarV2('text/secondary'),
          }}
        >
          {onFailureLabel}
        </span>
      )}
      {abandonedLabel && (
        <span
          data-testid="abandoned-label"
          style={{
            fontSize: '11px',
            fontStyle: 'italic',
            color: cssVarV2('text/tertiary'),
          }}
        >
          {abandonedLabel}
        </span>
      )}
      <span
        style={{
          minWidth: '60px',
          textAlign: 'right',
          color: cssVarV2('text/tertiary'),
          fontSize: '11px',
        }}
      >
        {row.durationMs} ms
      </span>
    </div>
  );
};

function pickStatusVisuals(status: ProcessStageRowEntry['status']): {
  statusIcon: string;
  statusColor: string;
} {
  switch (status) {
    case 'success':
      return { statusIcon: '✓', statusColor: cssVarV2('status/success') };
    case 'failed':
      return { statusIcon: '✗', statusColor: cssVarV2('status/error') };
    case 'abandoned':
    case 'aborted':
      // sl-8fwi.3 rework: both the historical `abandoned` label and the
      // distinct `aborted` status render with the same visual.
      // The icon is the Unicode CIRCLED DIVISION SLASH (U+2298), not
      // an emoji — chosen so fonts that lack emoji support still
      // render a distinguishable glyph.
      return { statusIcon: '⊘', statusColor: cssVarV2('text/tertiary') };
  }
}
